import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { ofRoute, zefGo } from '@zerops/zef/ngrx-router';
import { ServiceStack } from '@zerops/models/service-stack';
import { ServiceStackEntity } from '@zerops/zerops/core/service-stack-base';
import { isControlPlaneService, parseAuthorizedAgents } from '@zerops/zerops/core/zerops-services';
import { storeUserDataSuccess, UserEntity } from '@zerops/zerops/core/user-base';
import { UserDataEntity } from '@zerops/zerops/core/user-data-base';
import { PROJECT_BASE_FEATURE_NAME } from '@zerops/zerops/core/project-base';
import { DEFAULT_APP_ROUTE } from '@zerops/zerops/app';
import { environment } from '@zerops/zerops/env';
import {
  setZcpPoolClaimCookie,
  clearZcpPoolClaimCookie,
  hasPendingZcpPoolClaim
} from './zcp-pool-claim-base.util';
import { ZcpOnboardWizardService } from './zcp-onboard-wizard.service';

// If the ZCP never surfaces in the eagerly-subscribed service-stack list within
// this window, give up and drop the claim so the user just stays on wherever
// the default post-auth redirect landed them.
const ZCP_CLAIM_NAVIGATE_TIMEOUT = 30000;

@Injectable()
export class ZcpPoolClaimBaseEffect {
  // # Deps
  #actions$ = inject(Actions);
  #store = inject(Store);
  #serviceStackEntity = inject(ServiceStackEntity);
  #userEntity = inject(UserEntity);
  #userDataEntity = inject(UserDataEntity);
  #overlay = inject(ZcpOnboardWizardService);

  // # Effects
  /**
   * Capture the claim the moment the user lands on login/registration carrying
   * `?zcp=true`. Persisted to a cookie (not the store) so it survives our own
   * GitHub/GitLab button redirect (which doesn't forward the param) and the
   * post-signup navigation — both of which wipe the in-memory store.
   *
   * A param-less landing on either route clears any pending cookie instead.
   * The cookie outlives a single visit (10-minute max-age), so a user who
   * opens `?zcp=true`, then abandons that tab before the drain's own clear
   * runs (`#resolveAndEnterSelection$`, below), leaves it pending. Without
   * this clear, a later, wholly unrelated login/registration in the same
   * browser within that window would get silently swept into the claim
   * drain — a full-screen wizard wait for a ZCP stack that was never
   * requested. Landing on either route without the param is the user
   * leaving the claim flow, so it is treated as an explicit exit. Accepted
   * tradeoff: the registration/login cross-links drop query params, so an
   * existing-account user bounced from a `?zcp=true` registration page to
   * plain `/login` loses the claim too — acceptable since the claim links
   * only ever target signup.
   */
  onAuthRouteSetClaimCookie$ = createEffect(() => this.#actions$.pipe(
    ofRoute<{ zcp?: string }>([ 'login', 'registration' ]),
    tap(({ payload: { routerState: { queryParams } } }) => {
      if (queryParams?.zcp === 'true') { setZcpPoolClaimCookie(); }
      else { clearZcpPoolClaimCookie(); }
    })
  ), { dispatch: false });

  /**
   * Once fully authed (and org-resolved), if a claim is pending, run the
   * shared resolve-and-enter-selection tail (`#resolveAndEnterSelection$`).
   * Gates mirror the recipe-handoff drain — `refresh !== true` skips
   * org-switch reloads, `clientUserId` skips zero-active-account logins.
   */
  onFullyAuthedNavigateToZcp$ = createEffect(() => this.#actions$.pipe(
    ofType(storeUserDataSuccess),
    filter(({ refresh, clientUserId }) => refresh !== true && !!clientUserId),
    filter(() => hasPendingZcpPoolClaim()),
    switchMap(() => this.#resolveAndEnterSelection$())
  ));

  /**
   * DEV-ONLY (`environment.simulateZcpPoolClaim`). Re-runs the exact same
   * resolve-and-enter-selection tail on every reload for the logged-in
   * account — no `?zcp=true` signup needed — as long as the account has a
   * running ZCP, so the wizard can be iterated on without a fresh signup each
   * time. Fires once auth resolves (`activeClientUser$` gated on a real
   * `clientId`, `take(1)`); a reload re-triggers naturally through the same
   * app-boot sequence. A permanent no-op when the flag is off, i.e. every
   * build that doesn't set the var (prod deploys don't) — deliberately a
   * freshly-constructed empty observable (`of<Action>()`), not the shared
   * `EMPTY` singleton: `createEffect` tags its returned observable with a
   * non-configurable property, and every test in this file constructs a new
   * `ZcpPoolClaimBaseEffect`, so a shared singleton throws "Cannot redefine
   * property" on the second instantiation. Doesn't gate on the claim cookie:
   * the shared tail clears it unconditionally on both success and timeout,
   * which is a harmless no-op when none was set.
   */
  onDevSimulateClaim$ = createEffect(() => environment.simulateZcpPoolClaim !== 'true'
    ? of<Action>()
    : this.#userEntity.activeClientUser$.pipe(
      filter((clientUser) => !!clientUser?.clientId),
      take(1),
      switchMap(() => this.#resolveAndEnterSelection$())
    ));

  // # Streams
  /**
   * Raise the wizard cover, wait for the precreated project's ZCP to appear
   * in the eager service-stack list, open its Cloud IDE data, and hand off to
   * the interactive agent-picking phase. `prewarm()` opens the code-server
   * overlay directly; off the control-plane page it has no placeholder to
   * dock into, so it presents full-screen over the project detail. Single-use:
   * the cookie is cleared as soon as we resolve (or time out) — harmless if
   * none was set, which lets the dev simulator (`onDevSimulateClaim$`) share
   * this exact tail instead of a parallel, driftable copy. The full-screen
   * claim overlay goes up the instant we start (hiding the dashboard flash,
   * the redirect, and the scroll-into-place); dismissal is the wizard's own
   * state machine (`ZcpOnboardWizardService`). Once the `-zagent` userData
   * resolves, the wizard advances `claiming` -> `picking`
   * (spec-welcome-mode.md §8.1) — the same `enterPicking` step the §8.2 dev
   * entry drives (project-detail.effect.ts `onZcpOnboardResolveStack$`). The
   * roster itself is the static `SUPPORTED_AGENT_TYPES` registry, rendered
   * directly by the component; only which of those ids are already authorized
   * is threaded through here. On timeout we drop the claim, reveal the app,
   * and fall back to the default route rather than stranding the user.
   */
  #resolveAndEnterSelection$(): Observable<Action> {
    this.#overlay.show();
    return combineLatest([
      // The client-wide service-stack subscription (service-stack-base.effect,
      // `handleGlobally: false`) stores its list UNTAGGED, so read the default
      // list with no tag — a FEATURE_NAME-tagged read returns an empty list.
      this.#serviceStackEntity.list$().pipe(
        map((stacks) => stacks?.find(isControlPlaneService)),
        filter((stack): stack is ServiceStack => !!stack)
      ),
      this.#userEntity.activeClientUser$.pipe(
        filter((clientUser) => !!clientUser?.clientId)
      )
    ]).pipe(
      take(1),
      // Kick off the ZCP's zagent user-data load now, behind the cover. The
      // overlay's URL resolver latches the FIRST non-empty list via take(1)
      // (`[]` is truthy), so the pre-warm must not open it until this data has
      // actually arrived — otherwise it locks onto an empty list, resolves to
      // "no VS Code", and never retries.
      tap(([ stack, clientUser ]) => this.#store.dispatch(
        this.#userDataEntity.listSubscribe(
          clientUser.clientId,
          { name: `${PROJECT_BASE_FEATURE_NAME}-zagent`, id: stack.projectId },
          {
            search: [
              { name: 'projectId', operator: 'eq', value: stack.projectId },
              { name: 'serviceStackName', operator: 'eq', value: stack.name }
            ]
          }
        )
      )),
      switchMap(([ stack ]) => this.#userDataEntity.list$(
        { name: `${PROJECT_BASE_FEATURE_NAME}-zagent`, id: stack.projectId }
      ).pipe(
        map((userData) => userData?.filter((itm) => itm.serviceStackId === stack.id) ?? []),
        filter((userData) => userData.length > 0),
        take(1),
        map((userData) => ({ stack, userData }))
      )),
      timeout(ZCP_CLAIM_NAVIGATE_TIMEOUT),
      tap(({ stack, userData }) => {
        clearZcpPoolClaimCookie();
        // User-data is in — open the code-server iframe now, behind the cover,
        // in parallel with the navigation below. On the project detail page
        // (no dock placeholder) the overlay presents full-screen.
        this.#overlay.prewarm(stack.id);
        // Advance `claiming` -> `picking` (spec-welcome-mode.md §8.1),
        // matching the §8.2 dev entry's `enterPicking` step. Without this the
        // wizard stalled at `claiming` forever: the embed prewarmed behind
        // the layer but the picker never showed. This userData emission also
        // carries which of the static roster's ids are already authorized
        // (§8.2) — threaded through so an already-authorized pick can skip
        // `authorizing`.
        this.#overlay.enterPicking(parseAuthorizedAgents(userData));
      }),
      map(({ stack }) => zefGo([ '/project', stack.projectId ])),
      catchError(() => {
        clearZcpPoolClaimCookie();
        this.#overlay.hide();
        return of(zefGo(DEFAULT_APP_ROUTE));
      })
    );
  }
}
